/**
 * @file Table Array Editor (grid with modal editing)
 * @version 1.0.0
 */

import GridArrayEditor, { GRID_HEADERS_TEMPLATE } from './GridArrayEditor.js';
import {
  ARRAY_HEADER_TEMPLATE,
  ARRAY_ITEM_CONTROLS_TEMPLATE
} from './ArrayEditor.js';
import JSONEditor from './JSONEditor.js';
import { createDefaultDocument } from './schemas.js';
import { registerEditor } from './component-registry.js';

const TableArrayEditor = {
  name: 'TableArrayEditor',
  extends: GridArrayEditor,

  setup(props, ctx) {
    return GridArrayEditor.setup(props, ctx);
  },

  components: {
    ...GridArrayEditor.components,
    'modal-object-editor': JSONEditor,
  },

  props: {
    modalEditorProps: { required: false },
    modalEditorComponent: { required: false },
    mode: { default: 'edit' },
    deleteItem: { required: false, default: null },
  },

  data() {
    return {
      modalItemIndex: undefined, // undefined = closed, null = create, number = edit
      modalInitialValue: undefined,
    };
  },

  computed: {
    /**
     * Override childMode - always display in table mode
     */
    childMode() {
      return 'display';
    },

    /**
     * Check if modal is open
     */
    isModalOpen() {
      return this.modalItemIndex !== undefined;
    },

    /**
     * Modal mode: 'create' or 'edit'
     */
    modalMode() {
      if (this.modalItemIndex === undefined) return null;
      return this.modalItemIndex === null ? 'create' : 'edit';
    },

    /**
     * Modal value
     */
    modalValue() {
      return this.modalInitialValue;
    },

    /**
     * Modal title
     */
    modalTitle() {
      return this.modalMode === 'create' ? 'Add Item' : 'Edit Item';
    },

    /**
     * Modal editor props - can be overridden in child components
     */
    resolvedModalEditorProps() {
      if (this.modalEditorProps) {
        return this.modalEditorProps(
          this.modalItemIndex,
          this.modalInitialValue
        );
      }
      console.log('default props');
      return {
        schema: this.getItemSchema(),
        value: this.modalValue,
        reference:
          this.modalItemIndex !== null
            ? this.getItemReference(this.modalItemIndex)
            : undefined,
        mode: 'form',
        displayLabel: false,
      };
    },
  },

  methods: {
    /**
     * Override addItem to open modal
     */
    addItem() {
      this.openCreateModal();
    },

    /**
     * Override removeItem to delegate to deleteItem prop when provided
     * @param {number} displayIndex
     */
    removeItem(displayIndex) {
      if (this.deleteItem) {
        const item = this.getItemValue(displayIndex);
        this.deleteItem(item);
      } else {
        const sourceIndex = this.getSourceIndex(displayIndex);
        const orderIndex = this.itemsOrder.indexOf(sourceIndex);
        if (orderIndex !== -1) {
          this.itemsOrder.splice(orderIndex, 1);
        }
        this.childStates.delete(sourceIndex);
        this.emitState();
      }
    },

    /**
     * Open edit modal
     * @param displayIndex
     */
    openEditModal(displayIndex) {
      this.modalItemIndex = displayIndex;
      this.modalInitialValue = this.getItemValue(displayIndex);
    },

    /**
     * Open create modal
     */
    openCreateModal() {
      this.modalItemIndex = null;
      this.modalInitialValue = createDefaultDocument(
        this.getItemSchema(),
        this.getAttribute
      );
    },

    /**
     * Close modal
     */
    closeModal() {
      this.modalItemIndex = undefined;
      this.modalInitialValue = undefined;
    },

    /**
     * Handle modal input (form save)
     * @param itemState
     */
    handleModalInput(itemState) {
      if (itemState.state !== 'updated' || !itemState.update) return;

      const savedValue = itemState.update(this.modalInitialValue);

      if (this.modalMode === 'create') {
        this.internalValue.editable.push(savedValue);
        this.itemsOrder.push(this.internalValue.editable.length - 1);
      } else {
        const sourceIndex = this.getSourceIndex(this.modalItemIndex);
        this.internalValue.editable[sourceIndex] = savedValue;
      }

      this.closeModal();
      this.emitState();
    },
  },

  template: `
    <div class="array-editor" v-if="canRead" v-bind="$attrs">
      ${ARRAY_HEADER_TEMPLATE}

      <!-- Items content -->
      <div v-if="!isUndefined" class="array-items">
        <div class="array-grid" :style="gridLayoutStyles" :key="gridFieldNames.length">
          <!-- Grid headers (with optional sort control via slot, includes filter header) -->
          <slot
            name="columns-titles"
            :fields="gridFieldNames"
            :getFieldTitle="getFieldTitle"
            :actionControlStyles="actionControlStyles"
            :fieldHeaderStyles="fieldHeaderStyles"
          >
            ${GRID_HEADERS_TEMPLATE}
          </slot>

          <!-- Grid items -->
          <template v-for="(sourceIndex, displayIndex) in displayIndices" :key="sourceIndex">
            <component
              :is="getItemComponent(getItemSchema())"
              :schema="getItemSchema()"
              :value="getItemValue(displayIndex)"
              :reference="getItemReference(displayIndex)"
              :mode="childMode"
              :deletable="false"
              :displayLabel="false"
              :displayFieldsLabels="false"
              :controlStyles="actionControlStyles"
              :vertical="!vertical"
              @input="(state) => handleItemInput(displayIndex, state)"
            >
              <template #controls>
                <div class="d-flex gap-1 align-items-center">
                  <!-- Edit button (table-specific) -->
                  <button
                    @click="openEditModal(displayIndex)"
                    class="btn btn-outline-secondary btn-sm"
                    title="Edit item"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>

                  ${ARRAY_ITEM_CONTROLS_TEMPLATE}
                </div>
              </template>
            </component>
          </template>
        </div>

        <!-- Empty state -->
        <div v-if="isEmpty" class="text-muted fst-italic py-1">
          Empty
        </div>

        <!-- Pagination controls -->
        <pagination-controls v-if="isPaginationVisible" />
      </div>

      <!-- Modal -->
      <div v-if="isModalOpen" class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ modalTitle }}</h5>
              <button type="button" class="btn-close" @click="closeModal"></button>
            </div>
            <div class="modal-body">
              <component
                :is="modalEditorComponent ?? 'modal-object-editor'"
                v-if="modalItemIndex !== undefined"
                v-bind="resolvedModalEditorProps"
                @input="handleModalInput"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="isModalOpen" class="modal-backdrop fade show"></div>
    </div>
  `,
};

registerEditor('array', 'table', TableArrayEditor);

export default TableArrayEditor;
